'use client'
import { useState } from "react"
import { numberFormatter, truncateToTwoDecimals } from "../../lib/util"
import ItemDescModal from './ItemDescModal'
import { useSelector } from "react-redux"

const InvoiceTableRow = ({ invoiceItem, index, currency, userLocale, setAllRows }) => {
  const [showModal, setShowModal] = useState(false)
  const [shortDescription, setShortDescription] = useState(() => {
    if (invoiceItem?.["item-description"]?.length) {
      const shortDesc = invoiceItem?.["item-description"]?.length > 30
      ? invoiceItem?.["item-description"].substring(0, 30) + '...'
      : invoiceItem?.["item-description"]
      return shortDesc
    }
    return ''
  })
  const [description, setDescription] = useState(invoiceItem?.["item-description"] || "")
  const [isPercentage, setIsPercentage] = useState(invoiceItem["item-tax-type"] === "percentage")

  const percentageBasedValue = truncateToTwoDecimals(invoiceItem["item-tax"] * invoiceItem["item-quantity"] * invoiceItem["item-unit-price"] / 100)
  const fixedBasedValue = truncateToTwoDecimals(invoiceItem["item-tax"] * invoiceItem["item-quantity"])

  const calculateTaxt = () => isPercentage ? percentageBasedValue : fixedBasedValue

  let subtotal = truncateToTwoDecimals(invoiceItem["item-quantity"]) * truncateToTwoDecimals(invoiceItem["item-unit-price"]) + truncateToTwoDecimals(calculateTaxt())
  
  const formattedNumber = numberFormatter(subtotal, userLocale)

  const handleInputChange = (e, val) => {
    const updatedInvoiceItem = { ...invoiceItem }
    updatedInvoiceItem[e.target.name] = val

    const percentageBasedValue = truncateToTwoDecimals(updatedInvoiceItem["item-tax"] * updatedInvoiceItem["item-quantity"] * updatedInvoiceItem["item-unit-price"] / 100)
    const fixedBasedValue = truncateToTwoDecimals(updatedInvoiceItem["item-tax"] * updatedInvoiceItem["item-quantity"])

    updatedInvoiceItem["tax-fixed-value"] = isPercentage ? percentageBasedValue : fixedBasedValue
    updatedInvoiceItem["subtotal"] = truncateToTwoDecimals(updatedInvoiceItem["item-quantity"] * updatedInvoiceItem["item-unit-price"] + updatedInvoiceItem["tax-fixed-value"])
    
    setAllRows((prev) => {
      const updatedRows = [...prev]
      updatedRows[index] = updatedInvoiceItem
      return updatedRows
    })
  }

  const handleTaxToggle = () => {
    setIsPercentage(!isPercentage)
    const taxTypeToSet = isPercentage ? "fixed" : "percentage"

    const percentageBasedValue = truncateToTwoDecimals(invoiceItem["item-tax"] * invoiceItem["item-quantity"] * invoiceItem["item-unit-price"] / 100)
    const fixedBasedValue = truncateToTwoDecimals(invoiceItem["item-tax"] * invoiceItem["item-quantity"])
    
    const taxFixedValue = taxTypeToSet === "percentage" ? percentageBasedValue : fixedBasedValue

    const rowSubTotal = truncateToTwoDecimals(invoiceItem["item-quantity"] * invoiceItem["item-unit-price"] + taxFixedValue)
    const updatedInvoiceItem = { ...invoiceItem }
    updatedInvoiceItem["item-tax-type"] = taxTypeToSet
    updatedInvoiceItem["tax-fixed-value"] = taxFixedValue
    updatedInvoiceItem["subtotal"] = rowSubTotal
    setAllRows((prev) => {
      const updatedRows = [...prev]
      updatedRows[index] = updatedInvoiceItem
      return updatedRows
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    const shortDesc = description.length > 30
    ? description.substring(0, 30) + '...'
    : description

    setShortDescription(shortDesc)

    // Update the allRows array with the description
    setAllRows((prev) => {
      const updatedRows = [...prev]
      updatedRows[index] = {
        ...updatedRows[index],
        "item-description": description
      }
      return updatedRows
    })

    setShowModal(false)
  }

  const handleCancel = () => {
    setDescription(invoiceItem?.["item-description"] || "")
    setShowModal(false)
  }

  return (
    <>
      <div className={`${invoiceItem?.hasErrors ? 'border-l-4 border-red-500' : ''} grid grid-cols-12 gap-4 content-start sm:mt-2 mt-7 hover:bg-gray-50 px-6 transition-colors rounded-sm cursor-pointer`}>
        <div className="sm:col-span-4 col-span-12">
          <div className="sm:hidden text-secondary text-sm font-semibold mb-1">Item/Service name</div>
          <input 
            name="item-name"
            id={`item-name-csr-${index}`}
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent itemName-csr"
            value={invoiceItem["item-name"] || ""}
            onChange={(e)=> handleInputChange(e, e.target.value)}
            data-for="item-name"
            placeholder="Item/Service name"
          />
          <button id={`item-description-btn-csr-${index}`} type="button" className="text-sm text-blue-600 underlined mt-1 ml-1" onClick={() => setShowModal(true)}>
            {invoiceItem?.["item-description"] ? 'Edit description' : 'Add description'}
          </button>
          <p id={`item-description-csr-${index}`} className="text-sm text-gray-400 mt-1 ml-1 min-h-5">{shortDescription}</p>
        </div>
        <div className="sm:col-span-2 col-span-6">
          <div className="sm:hidden text-secondary text-sm font-semibold mb-1">Qty.</div>
          <input
            name="item-quantity"
            id={`item-quantity-csr-${index}`}
            type="number"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent itemQuantity-csr"
            value={invoiceItem["item-quantity"] || ""}
            placeholder={invoiceItem["item-quantity-placeholder"] || "0"}
            onChange={(e) => handleInputChange(e, e.target.valueAsNumber || 0)}
            data-for="item-quantity"
            step="0.01"
          />
        </div>
        <div className="sm:col-span-2 col-span-6">
          <div className="sm:hidden text-secondary text-sm font-semibold mb-1">Unit Price</div>
          <input 
            name="item-unit-price"            
            id={`item-unit-price-csr-${index}`}
            type="number"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent itemUnitPrice-csr"
            step="0.01"
            placeholder={invoiceItem["item-unit-price-placeholder"] ? invoiceItem["item-unit-price-placeholder"]+".00" : "0.00"}
            value={invoiceItem["item-unit-price"] || ""}
            onChange={(e) => handleInputChange(e, e.target.valueAsNumber || 0)}
            data-for="item-unit-price"
          />
        </div>
        <div className="sm:col-span-2 col-span-6">
          <div className="sm:hidden text-secondary text-sm font-semibold mb-1"> Tax </div>
          <input 
            name="item-tax"
            id={`item-tax-csr-${index}`}
            type="number"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent itemTax-csr"
            value={invoiceItem["item-tax"] || ""}
            placeholder={invoiceItem["item-tax"] || "0.00"}
            onChange={(e) => handleInputChange(e, e.target.valueAsNumber || 0)}
            step="0.01"
            data-for="item-tax"
          />
          <div className="md:flex items-center mt-1.5 relative">
            <div className="flex items-center font-semibold sm:font-normal">
              <button
                onClick={handleTaxToggle}
                type="button"
                className={`sm:px-2 sm:py-0.5 px-3 py-1 text-xs rounded-l-md ${isPercentage ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                %
              </button>
              <button
                onClick={handleTaxToggle}
                type="button"
                className={`relative group sm:px-2 flex sm:py-0.5 px-3 py-1 text-xs rounded-r-md ${!isPercentage ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                Fixed
                <svg className={`w-4 h-4 ${isPercentage ? 'text-gray-400 hover:text-gray-600' : 'text-white'} cursor-help ml-1`}
                  fill="currentColor" viewBox="0 0 20 20" title="Fixed tax is a specific amount added to each item, regardless of the item's price">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-20">
                  <div className="whitespace-nowrap rounded-md bg-white text-black px-3 py-1.5 shadow-sm border border-gray-200 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
                    Fixed = Tax × Quantity
                    <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45 border border-gray-200"></span>
                  </div>
                </div>
              </button>
            </div>
            <p id={`tax-fixed-value-ssg${index}`} className="text-xs text-gray-400 mt-1 md:ml-2 md:mt-0 md:min-w-[120px]">
              {`${currency} ${numberFormatter(calculateTaxt(), userLocale)}`}
            </p>
          </div>
        </div>
        <div className="sm:col-span-2 col-span-6">
          <div className="sm:hidden text-secondary text-sm font-semibold mb-1">Sub-total</div>
          <span id={`subtotal-csr-${index}`} className="font-mono text-gray-900 subtotal-csr" data-for="subtotal">{formattedNumber}</span>
        </div>
      </div>
      {showModal && (
        <ItemDescModal
          description={description}
          setDescription={setDescription}
          isEditing={invoiceItem?.["item-description"]?.length > 0}
          handleCancel={handleCancel}
          handleSubmit={handleSubmit}
          invoiceItem={invoiceItem}
          setAllRows={setAllRows}
          index={index}
        />
      )}
    </>
)}

export default function InvoiceTable({ allRows, setAllRows }) {
  const userLocale = navigator.language || navigator.userLanguage  
  const currency = useSelector((state) => state.invoice.currency) 
  
  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm px-1">
        <div className="hidden sm:block bg-gray-50 px-6 py-4">
          <div className="grid grid-cols-12 gap-4 text-sm font-semibold text-gray-700">
            <div className="sm:col-span-4 col-span-12">Item/Service name</div>
            <div className="sm:col-span-2 col-span-6">Qty.</div>
            <div className="sm:col-span-2 col-span-6">Unit price</div>
            <div className="sm:col-span-2 col-span-6 flex items-center">Tax</div>
            <div className="sm:col-span-2 col-span-6">Subtotal</div>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          <div className="invoice-row-ssg">
            <div className="pb-2 invoice-row-csr">
              {allRows.map((el, i) => (
                <InvoiceTableRow 
                  invoiceItem={el} 
                  setAllRows={setAllRows} 
                  key={i} 
                  currency={currency} 
                  userLocale={userLocale} 
                  index={i} 
                />
                ))}
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button type="button" 
                onClick={()=> setAllRows([...allRows, {
                  "item-name": "",
                  "item-quantity": "",
                  "item-unit-price": "",
                  "item-description": "",
                  "item-tax": "",
                  "item-tax-type": "percentage",
                  "tax-fixed-value": ""
                  }])} 
                data-role="add-row" 
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium transition-colors cursor-pointer">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Add new invoice item</span>
              </button>
              {(allRows.length > 1) && (
              <button type="button" onClick={()=> setAllRows(allRows.slice(0, -1))} data-role="add-row" className="flex items-center space-x-2 text-red-500 hover:text-red-600 font-medium transition-colors cursor-pointer mt-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 14">
                  <path d="M3 8a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9A.5.5 0 0 1 3 8z" />
                </svg>
                <span>Remove last item</span>
              </button>
            )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}